'use client';

import React from 'react';
import Image from 'next/image';
import Link from 'next/link';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { toast } from 'sonner';
import { FaInstagram, FaXTwitter } from 'react-icons/fa6';
import { newsletterFooterSchema } from '@/lib/ValidationSchemas';
import { useNewsletterSubscribe } from '@/hooks/useNewsletterSubscribe';

function Footer() {
  const newsletterMutation = useNewsletterSubscribe();

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: zodResolver(newsletterFooterSchema),
    defaultValues: { email: '' },
  });

  const onNewsletterSubmit = (data) => {
    newsletterMutation.mutate(
      { email: data.email, source: 'footer' },
      {
        onSuccess: (res) => {
          toast.success(res?.message || "You're on the list.");
          reset();
        },
        onError: (err) => {
          const apiMessage = err?.response?.data?.error;
          if (Array.isArray(apiMessage) && apiMessage[0]?.message) {
            toast.error(apiMessage[0].message);
          } else if (typeof apiMessage === 'string') {
            toast.error(apiMessage);
          } else {
            toast.error('Could not subscribe. Please try again.');
          }
        },
      }
    );
  };

  const busy = isSubmitting || newsletterMutation.isPending;

  return (
    <footer className="relative bg-[#1A1A1A] text-white overflow-hidden overflow-x-hidden py-10 w-full flex justify-center">

      {/* Glow Backgrounds */}
      <Image
        src="/images/footer/black_glow.svg"
        alt="Purple Glow"
        width={500}
        height={500}
        className="absolute left-0 top-1/3 -translate-y-1/2 z-0 pointer-events-none w-[min(100vw,500px)] h-auto opacity-60 md:opacity-100"
      />
      <Image
        src="/images/footer/green_glow.svg"
        alt="Green Glow"
        width={500}
        height={500}
        className="absolute right-0 top-1/4 md:top-1/3 -translate-y-1/2 z-0 pointer-events-none w-[min(100vw,500px)] h-auto opacity-60 md:opacity-100"
      />

      {/* Inner Container */}
      <div className="relative w-full max-w-[1512px] z-10 px-[10px] sm:px-[40px] md:px-[72px] lg:px-[120px] mt-6 overflow-x-hidden">

        {/* CTA Box */}
        <div className="flex flex-col justify-center bg-[#7373730F] rounded-2xl border border-white/10 px-4 py-6 sm:px-8 md:p-16 md:text-center text-left backdrop-blur-md mx-auto min-h-[220px] md:min-h-[360px]">
          <h2 className="text-[clamp(1.5rem,4vw,3rem)] font-bold mb-2 text-center">Ready to get started?</h2>
          <p className="text-white/70 mb-6 text-[clamp(0.9rem,1.5vw,25px)] text-center">To join the worldwide community</p>

          <div className="w-full max-w-[582px] mx-auto">
          <form
            onSubmit={handleSubmit(onNewsletterSubmit)}
            className={`flex items-center min-h-[44px] md:min-h-[52px] rounded-full bg-[#1A1A1A] p-1 md:p-1.5 overflow-hidden gap-1 md:gap-2 border transition-colors ${
              errors.email
                ? 'border-red-500'
                : 'border-transparent'
            }`}
          >
            <input
              type="email"
              autoComplete="email"
              placeholder="Enter your email"
              disabled={busy}
              aria-invalid={errors.email ? 'true' : 'false'}
              className="flex-1 min-w-0 bg-transparent px-4 md:px-6 py-2 text-white text-base md:text-[20px] placeholder-white/60 focus:outline-none disabled:opacity-60"
              style={{ fontSize: '16px' }}
              {...register('email')}
            />
            <button
              type="submit"
              disabled={busy}
              className="shrink-0 whitespace-nowrap bg-gradient-to-r from-gradientStart to-gradientEnd text-black text-xs sm:text-sm md:text-base px-4 md:px-6 py-2 md:py-3 rounded-full disabled:opacity-60"
            >
              {busy ? '…' : 'Join Now'}
            </button>
          </form>
          </div>
        </div>

        {/* Social icons — above divider */}
        <div className="mt-16 flex justify-end max-w-[1512px] mx-auto pb-4">
          <div className="flex items-center gap-3">
            <a
              href="https://www.instagram.com/propradarlive"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Prop Radar on Instagram"
              className="text-white/80 hover:text-white transition-colors"
            >
              <FaInstagram className="size-5" />
            </a>
            <a
              href="https://x.com/propradarlive"
              target="_blank"
              rel="noopener noreferrer"
              aria-label="Prop Radar on X"
              className="text-white/80 hover:text-white transition-colors"
            >
              <FaXTwitter className="size-5" />
            </a>
          </div>
        </div>

        {/* Navigation */}
        <div className="border-t border-white/10 pt-8 text-sm flex flex-col md:flex-row flex-wrap justify-center md:justify-between items-center gap-6 max-w-[1512px] mx-auto">
          <div className="flex items-center gap-2">
            <img src="/images/header/white_logo.svg" alt="Logo" className="w-10 h-10" />
            <span className="font-semibold text-2xl">Prop Radar</span>
          </div>
          <div className="flex flex-col md:flex-row md:items-start items-center gap-6 md:gap-12 text-white/80">
            <nav className="flex flex-wrap justify-center gap-x-4 gap-y-2 sm:gap-x-6 md:gap-x-8">
              <Link href="/" className="hover:text-white font-semibold text-sm sm:text-[16px]">Home</Link>
              <Link href="/compare" className="hover:text-white text-sm sm:text-[16px]">Compare</Link>
              <Link href="/discount" className="hover:text-white text-sm sm:text-[16px]">Discount</Link>
              <Link href="/firm-listing" className="hover:text-white text-sm sm:text-[16px]">Propfirms</Link>
              <Link href="/contact" className="hover:text-white text-sm sm:text-[16px]">Contact</Link>
            </nav>

            <nav className="flex flex-wrap justify-center gap-x-4 gap-y-2 sm:gap-x-6 md:gap-x-8">
              <Link href="/privacy-policy" className="hover:text-white text-sm sm:text-[16px]">Privacy Policy</Link>
              <Link href="/terms-of-service" className="hover:text-white text-sm sm:text-[16px]">Terms of Service</Link>
            </nav>
          </div>
        </div>

        {/* Copyright */}
        <div className="flex justify-center mt-5 max-w-[1200px] mx-auto">
          <p className="text-white/80 md:text-[14px] text-[12px] text-center md:text-right w-full md:w-auto">
            Copyright © 2025 Prop Radar. All Rights Reserved.
          </p>
        </div>
      </div>
    </footer>
  );
}

export default Footer;
